// src/components/admin/BranchAssignment.tsx
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { userService } from "../../services/users";
import { getBranches } from "../../services/branches";
import { User } from "../../types/user";
import {
  Building2,
  X,
  Plus,
  MapPin,
  Phone,
  AlertCircle,
  Check,
} from "lucide-react";

interface BranchAssignmentProps {
  user: User;
  onClose: () => void;
}

// Simplified branch interface matching the API response
interface UserBranch {
  _id: string;
  name: string;
  code: string;
  address?: string;
  phone?: string;
  status: string;
}

export const BranchAssignment: React.FC<BranchAssignmentProps> = ({
  user,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Fetch user's current branches
  const { data: userBranchesData, isLoading: loadingUserBranches } = useQuery({
    queryKey: ["userBranches", user._id],
    queryFn: () => userService.getUserBranches(user._id),
  });

  // Fetch all branches
  const { data: allBranchesData, isLoading: loadingAllBranches } = useQuery({
    queryKey: ["branches"],
    queryFn: () => getBranches({}),
  });

  const currentBranches = userBranchesData?.data?.branches || [];
  const allBranches = allBranchesData?.data || [];
  const primaryBranch = userBranchesData?.data?.primaryBranch;

  // Assign branches mutation
  const assignMutation = useMutation({
    mutationFn: (branchIds: string[]) =>
      userService.assignBranches(user._id, branchIds),
    onSuccess: () => {
      setSuccess("Branches assigned successfully");
      setError("");
      setSelectedBranches([]);
      queryClient.invalidateQueries({ queryKey: ["userBranches", user._id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setTimeout(() => {
        setSuccess("");
      }, 3000);
    },
    onError: (error: any) => {
      setError(error.message || "Failed to assign branches");
      setSuccess("");
    },
  });

  // Remove branch mutation
  const removeMutation = useMutation({
    mutationFn: (branchId: string) =>
      userService.removeBranch(user._id, branchId),
    onSuccess: () => {
      setSuccess("Branch removed successfully");
      setError("");
      queryClient.invalidateQueries({ queryKey: ["userBranches", user._id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setTimeout(() => {
        setSuccess("");
      }, 3000);
    },
    onError: (error: any) => {
      setError(error.message || "Failed to remove branch");
      setSuccess("");
    },
  });

  const handleToggleBranch = (branchId: string) => {
    setSelectedBranches((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId]
    );
  };

  const handleAssignBranches = () => {
    if (selectedBranches.length === 0) {
      setError("Please select at least one branch");
      return;
    }

    // Combine current branches with newly selected ones
    const currentBranchIds = currentBranches.map((b: UserBranch) => b._id);
    const combinedBranchIds = [
      ...new Set([...currentBranchIds, ...selectedBranches]),
    ];

    assignMutation.mutate(combinedBranchIds);
  };

  const handleRemoveBranch = (branchId: string) => {
    if (currentBranches.length === 1) {
      setError(
        "Cannot remove the last branch. User must have at least one branch."
      );
      return;
    }
    removeMutation.mutate(branchId);
  };

  const isLoading = loadingUserBranches || loadingAllBranches;
  const isBranchAssigned = (branchId: string) =>
    currentBranches.some((b: UserBranch) => b._id === branchId);

  return (
    <div className="fixed inset-0 bg-black/80 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Building2 className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-xl font-semibold text-white">
                Manage Branch Assignments
              </h2>
              <p className="text-blue-100 text-sm">
                {user.firstName} {user.lastName} ({user.email})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-lg p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Current Branches */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                  Currently Assigned Branches ({currentBranches.length})
                </h3>
                {currentBranches.length === 0 ? (
                  <p className="text-gray-500 text-sm italic bg-gray-50 p-4 rounded-lg">
                    No branches assigned yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {currentBranches.map((branch: UserBranch) => (
                      <div
                        key={branch._id}
                        className={`border rounded-lg p-4 transition-all ${
                          branch._id === primaryBranch
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-semibold text-gray-900">
                                {branch.name}
                              </h4>
                              {branch._id === primaryBranch && (
                                <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                                  Primary
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                              Code: {branch.code}
                            </p>
                            {branch.address && (
                              <div className="flex items-start text-xs text-gray-500 mb-1">
                                <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                                <span>
                                  {typeof branch.address === "string"
                                    ? branch.address
                                    : `${branch.address.street || ""} ${
                                        branch.address.city || ""
                                      }`.trim() || "N/A"}
                                </span>
                              </div>
                            )}
                            {(branch.phone ||
                              (branch.contactInfo &&
                                branch.contactInfo.phone)) && (
                              <div className="flex items-center text-xs text-gray-500">
                                <Phone className="w-3 h-3 mr-1" />
                                <span>
                                  {branch.phone || branch.contactInfo?.phone}
                                </span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveBranch(branch._id)}
                            disabled={
                              removeMutation.isPending ||
                              currentBranches.length === 1
                            }
                            className={`ml-3 p-2 rounded-lg transition-colors ${
                              currentBranches.length === 1
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-red-50 text-red-600 hover:bg-red-100"
                            }`}
                            title={
                              currentBranches.length === 1
                                ? "Cannot remove last branch"
                                : "Remove branch"
                            }
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Branches */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Plus className="w-5 h-5 mr-2 text-green-600" />
                  Add Branches
                </h3>
                <div className="space-y-2 mb-4">
                  {allBranches
                    .filter(
                      (branch: any) =>
                        !isBranchAssigned(branch._id) &&
                        branch.status === "active"
                    )
                    .map((branch: any) => (
                      <label
                        key={branch._id}
                        className="flex items-start p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBranches.includes(branch._id)}
                          onChange={() => handleToggleBranch(branch._id)}
                          className="mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {branch.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            Code: {branch.abbreviation || branch.code || "N/A"}
                          </div>
                          {(branch.address ||
                            (branch.address && branch.address.street)) && (
                            <div className="flex items-start text-xs text-gray-500 mt-1">
                              <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                              <span>
                                {typeof branch.address === "string"
                                  ? branch.address
                                  : `${branch.address.street || ""} ${
                                      branch.address.city || ""
                                    }`.trim() || "N/A"}
                              </span>
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  {allBranches.filter(
                    (b: any) =>
                      !isBranchAssigned(b._id) && b.status === "active"
                  ).length === 0 && (
                    <p className="text-gray-500 text-sm italic bg-gray-50 p-4 rounded-lg">
                      All active branches are already assigned
                    </p>
                  )}
                </div>

                {selectedBranches.length > 0 && (
                  <button
                    onClick={handleAssignBranches}
                    disabled={assignMutation.isPending}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
                  >
                    {assignMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Assigning...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        <span>
                          Assign {selectedBranches.length} Branch
                          {selectedBranches.length !== 1 ? "es" : ""}
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
